/**
 * Inventory stat card.
 *
 * Ported from ipd/utils/IpdStatCard (which stays untouched — it is shared with the
 * rest of the app) with three fixes the module needs:
 *   1. height: '100%' — without it cards in a Grid row are not equal height, so a
 *      label that wraps to 3 lines leaves a ragged row.
 *   2. a circular skeleton while loading — the original renders no icon, so every
 *      card jumped 110 → 120px the moment data landed.
 *   3. tone tints the value, not just the icon halo — tone="red" on a failure count
 *      now actually reads as a failure.
 */
import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { IPD_COLORS, INV_TYPE, INV_ALPHA, cardSx } from '../theme';

const TONE_MAP = {
  default: IPD_COLORS.primary,
  teal: IPD_COLORS.primary,
  mint: IPD_COLORS.success.text,
  amber: IPD_COLORS.warning.text,
  red: IPD_COLORS.error.text,
  blue: IPD_COLORS.info.text,
};

/** Tones that mean "look at me" — these tint the number itself. */
const ALERT_TONES = new Set(['amber', 'red']);

const SKELETON_SX = { bgcolor: 'rgba(13, 35, 63, 0.08)' };

export default function StatCard({
  label, value, icon: Icon, tone = 'default', onClick, loading, selected = false,
}) {
  const color = TONE_MAP[tone] || IPD_COLORS.primary;
  const valueColor = ALERT_TONES.has(tone) && value ? color : IPD_COLORS.textPrimary;

  return (
    <Box
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-pressed={onClick ? selected : undefined}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        ...cardSx,
        p: 2.5,
        height: '100%',
        minHeight: 120,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        ...(selected ? {
          borderColor: IPD_COLORS.selectedBorder,
          backgroundColor: IPD_COLORS.selectedBg,
          boxShadow: IPD_COLORS.shadow.field,
        } : {}),
        '&:hover': onClick ? {
          borderColor: IPD_COLORS.primary,
          boxShadow: IPD_COLORS.shadow.cardHover,
        } : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        {loading ? (
          <Skeleton variant="text" width="60%" sx={SKELETON_SX} />
        ) : (
          <Typography sx={{ ...INV_TYPE.statLabel, color: IPD_COLORS.textSecondary, pr: 1 }}>
            {label}
          </Typography>
        )}
        {loading ? (
          <Skeleton variant="circular" width={40} height={40} sx={{ ...SKELETON_SX, flexShrink: 0 }} />
        ) : Icon && (
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: `${color}${INV_ALPHA.tint}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon sx={{ color, fontSize: 20 }} />
          </Box>
        )}
      </Box>
      {loading ? (
        <Skeleton variant="text" width="40%" height={40} sx={SKELETON_SX} />
      ) : (
        <Typography sx={{ ...INV_TYPE.statValue, color: valueColor }}>
          {value ?? '—'}
        </Typography>
      )}
    </Box>
  );
}
